import { Color, Spacing } from '@persona-id/design-tokens';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { CombinedStepType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import InquiryTemplatePreview from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplatePreview';
import { BuilderName, BuilderPreviewSteps } from 'dashboard/models/inquiry-template';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import { createMockCombinedStep } from 'inquiry/lib/stubs/inquiry';
import {
  AutoClassificationConfig,
  StepGovernmentId,
  StepPreview,
  StepSelfie,
  UiStepType,
} from 'lib/interfaces/StepsConfig';

const TemplatePageContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
`;

const LeftColumn = styled.div`
  width: 300px;
  flex: none;
  height: 100%;
  padding: ${Spacing[5]};
  border-right: 1px solid ${({ theme }) => theme.color.border.secondary};
`;

const TemplateDescription = styled(Paragraph)`
  line-height: 22px;
`;

const RightColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: auto;
  overflow: hidden;
`;

const RightColumn = styled.div`
  display: flex;
  flex: auto;
  overflow-x: auto;
  width: 100%;
  background-color: ${Color.CoreSlate50};
  align-items: center;
  justify-content: center;
`;

const BUTTON_HALF_WIDTH = 16;
const SLIDE_HALF_WIDTH = 200;
const SLIDE_HALF_SPACING = 32;
const BUTTON_OFFSET = BUTTON_HALF_WIDTH + SLIDE_HALF_SPACING + SLIDE_HALF_WIDTH;
const PreviewContainer = styled.div`
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding-bottom: ${Spacing[4]};
  flex: none;

  /* Values based on scaling factor to center in frame gutters */
  .slick-prev {
    left: calc(50% - ${BUTTON_OFFSET}px);
  }
  .slick-next {
    right: calc(50% - ${BUTTON_OFFSET}px);
  }
`;

const PageFooter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;

  height: ${Spacing[9]};
  padding: ${Spacing[4]};

  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
  width: 100%;
`;

const ConfirmationButton = styled(Button).attrs(() => ({ variant: 'primary' }))`
  width: 140px;
`;

interface CreateInquiryTemplatePageProps {
  builderName: BuilderName;
  description: string;
  stepsPreview: BuilderPreviewSteps | undefined;
  enabled: boolean;
  handleCreateInquiryTemplate: (builderName: string) => Promise<void>;
}

const CreateInquiryTemplatePage = ({
  builderName,
  description,
  stepsPreview,
  enabled,
  handleCreateInquiryTemplate,
}: CreateInquiryTemplatePageProps) => {
  const clientData = useMemo<StepPreview[]>(
    () =>
      (stepsPreview ?? []).map((stepPreview: StepPreview | CombinedStepType) => {
        if (
          stepPreview === UiStepType.Document ||
          stepPreview === UiStepType.GovernmentId ||
          stepPreview === UiStepType.Selfie ||
          stepPreview === UiStepType.SelfieReflection
        ) {
          const mockCombinedStep = createMockCombinedStep(stepPreview);
          if (stepPreview === UiStepType.Selfie) {
            // Remove the selfie consent text from the template preview
            (mockCombinedStep as StepSelfie).config.localizations['prompt-page'][
              'agree-to-policy'
            ] = '';
          } else if (
            stepPreview === UiStepType.GovernmentId &&
            [
              BuilderName.GovernmentIdWithAutoclassification,
              BuilderName.GovernmentIdWithAutoclassificationAndSelfie,
            ].includes(builderName)
          ) {
            (
              (mockCombinedStep as StepGovernmentId).config[
                'auto-classification-config'
              ] as AutoClassificationConfig
            )['is-enabled'] = true;
          }
          return mockCombinedStep;
        } else {
          return stepPreview;
        }
      }),
    [stepsPreview],
  );

  const createInquiryTemplate = useCallback(
    () => enabled && handleCreateInquiryTemplate(builderName),
    [builderName, enabled, handleCreateInquiryTemplate],
  );

  return (
    <TemplatePageContainer>
      <LeftColumn>
        <TemplateDescription>{description}</TemplateDescription>
      </LeftColumn>
      <RightColumnWrapper>
        <RightColumn>
          <PreviewContainer>
            <InquiryTemplatePreview clientData={clientData} scaleRatio={0.75} />
          </PreviewContainer>
        </RightColumn>
        <PageFooter>
          <ConfirmationButton
            id='create-template__confirmation-button'
            disabled={!enabled}
            onClick={createInquiryTemplate}
          >
            Use template
          </ConfirmationButton>
        </PageFooter>
      </RightColumnWrapper>
    </TemplatePageContainer>
  );
};

export default CreateInquiryTemplatePage;
